import '../../apps/b2b-ui/less/main.less';

// Sentry can't initialize until the async client-config fetch below resolves
// with a DSN, so nothing is listening for errors thrown while the imports
// below evaluate (in particular ./legacy's eager require.context load of the
// whole AngularJS app) or while that fetch is in flight. Import this first so
// its listeners are attached before anything riskier gets a chance to throw;
// buffered errors get replayed once Sentry actually initializes below.
import { flushEarlyErrors } from './early-error-capture';

// IMPORTANT: Must import @angular/compiler BEFORE any module that loads
// @angular/common (e.g. through HttpClient). The legacy require.context below
// eagerly loads b2b-ui which, via the AuthenticationService re-export, pulls
// in @angular/common and would otherwise fail with NG0202 / JIT compiler
// missing.
import '@angular/compiler';

import './legacy';

// Import the AngularJS app module to register all AngularJS components
// This must be imported BEFORE the hybrid bootstrap
import acme from '@b2b-ui';
import { initDevTools } from '@acme/b2b/devtools/bootstrap';
import * as Sentry from '@sentry/angular';

// Import hybrid bootstrap for Angular + AngularJS
import { bootstrapHybrid } from '@ng/main.hybrid';

(async () => {
  // Redirect root URL to /login before any routing code runs.
  // UIRouter sees /login from the start, avoiding the IGNORED initial transition.
  if (window.location.pathname === '/') {
    window.history.replaceState(null, '', '/login');
  }

  let sentryInitialized = false;

  try {
    const resp = await fetch('/v2/api/client-config');
    const config = await resp.json();
    window.LL_API_KEY = config.lunchLitely?.apiKey;
    window.CONFIG.lunchLitely.apiKey = window.LL_API_KEY;

    if (config.sentry?.dsn) {
      window.CONFIG.sentry = { dsn: config.sentry.dsn };
      Sentry.init({
        dsn: config.sentry.dsn,
        environment: window.CONFIG.environment,
        release: window.VERSION,
      });
      sentryInitialized = true;

      // TODO: remove once Sentry delivery is verified in GlitchTip/sandbox.
      // Visit any non-production page with ?sentry-debug appended to trigger a test event.
      if (
        window.CONFIG.environment !== 'production' &&
        new URLSearchParams(window.location.search).has('sentry-debug')
      ) {
        Sentry.captureException(new Error('Sentry frontend test error (acme-b2b-web)'));
      }
    }
  } catch (e) {
    // A failed/slow client-config fetch means Sentry (and the LunchLitely
    // apiKey) never initialize for this session, with no other signal that
    // error monitoring is silently a no-op - log loudly so that's visible.
    console.error(
      'Failed to load client-config; Sentry will not initialize for this session (error monitoring is a no-op)',
      e,
    );
  }

  // Replay anything buffered before Sentry could initialize (a throw during
  // the eager imports at the top of this file, or while the fetch above was
  // in flight) instead of losing it silently. Falls back to console.error
  // when Sentry never got a DSN so these still surface somewhere.
  flushEarlyErrors(
    sentryInitialized
      ? (error) => Sentry.captureException(error)
      : (error) => console.error('Early boot error (Sentry unavailable this session)', error),
  );

  bootstrapHybrid(acme);
  initDevTools();
})();
